import type {
  SourceSpan,
  StableId,
  SubprocessDecl,
} from "../types.js";
import type { ResolvedModel } from "../resolver.js";
import type { Flow } from "../process-model.js";
import type { StepKey, SubprocessDiff, Status } from "../diff-types.js";
import { moduleNameIndex, stepKeys } from "./processes.js";

/** Subprocess IR bodies keyed by source position AND owner. Steps are keyed off
 *  the RESOLVED body (§4.13.4) — the written step carries no caller for an
 *  omitted head, a `|` fan or a `\` snake, which would collapse every such step
 *  onto the same key. Stamped (type-template) instances share one source but
 *  are NOT interchangeable: an omitted head resolves to the stamping module, so
 *  each instance's resolved body calls out from its own owner. Keying on source
 *  alone diffs one instance against another's body, reporting a step change on
 *  an instance that never changed. */
function bodyBySource(model: ResolvedModel): Map<string, Flow> {
  const out = new Map<string, Flow>();
  for (const sm of model.subprocessModels ?? []) {
    const k = bodyKey(sm.source, sm.ownerPath);
    if (k && !out.has(k)) out.set(k, sm.body);
    // Source-only fallback for a lookup whose owner doesn't line up.
    const s = sourceKey(sm.source);
    if (s && !out.has(s)) out.set(s, sm.body);
  }
  return out;
}

function sourceKey(s: SourceSpan | undefined): string | undefined {
  return s ? `${s.file}:${s.start.offset}` : undefined;
}

function bodyKey(s: SourceSpan | undefined, owner: readonly string[] | undefined): string | undefined {
  const k = sourceKey(s);
  return k ? `${k}@${(owner ?? []).join(".")}` : undefined;
}

function bodyOf(sp: SubprocessDecl, bodies: Map<string, Flow>): Flow | undefined {
  const owned = bodyKey(sp.source, sp.ownerPath);
  const byOwner = owned ? bodies.get(owned) : undefined;
  if (byOwner) return byOwner;
  const k = sourceKey(sp.source);
  return k ? bodies.get(k) : undefined;
}

function keyEquals(a: StepKey, b: StepKey): boolean {
  return a.from === b.from && a.to === b.to;
}

function pathsEqual(a: readonly string[] | undefined, b: readonly string[] | undefined): boolean {
  if (!a && !b) return true;
  if (!a || !b) return false;
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false;
  return true;
}

/**
 * Diff subprocesses across two resolved models.
 *
 * Key strategy:
 *   - When both sides have a stable `id`, match by id (handles renames
 *     and owner moves cleanly).
 *   - Otherwise, fall back to qualified-name match
 *     (`<owner>.<name>` or bare `<name>` for free-standing).
 * Stamped instances (subprocesses inherited from a `type module …`
 * template) share the source `id`; we treat them as separate entries
 * keyed by `<id>@<owner-qualified-name>` so per-instance changes don't
 * collapse into a single noisy entry.
 */
export function diffSubprocesses(
  beforeModel: ResolvedModel,
  afterModel: ResolvedModel,
): Map<string, SubprocessDiff> {
  const beforeIndex = moduleNameIndex(beforeModel.modules);
  const afterIndex = moduleNameIndex(afterModel.modules);
  const beforeBodies = bodyBySource(beforeModel);
  const afterBodies = bodyBySource(afterModel);

  // Two-tier key strategy:
  //   - When a stable ID appears exactly once per side, key by ID alone so
  //     renames + owner-moves match as a single entry (ownerChange/nameChange).
  //   - When a stable ID is stamped across multiple instances (template
  //     inheritance), per-side counts > 1 — use `<id>@<owner>` so each
  //     instance is tracked separately.
  //   - When no stable ID, key by qualified name.
  const countById = (decls: readonly SubprocessDecl[]): Map<string, number> => {
    const m = new Map<string, number>();
    for (const sp of decls) if (sp.id) m.set(sp.id, (m.get(sp.id) ?? 0) + 1);
    return m;
  };
  const bCount = countById(beforeModel.subprocesses);
  const aCount = countById(afterModel.subprocesses);

  const keyOf = (sp: SubprocessDecl): string => {
    const owner = sp.ownerPath && sp.ownerPath.length > 0
      ? sp.ownerPath.join(".")
      : "";
    if (sp.id) {
      const bN = bCount.get(sp.id) ?? 0;
      const aN = aCount.get(sp.id) ?? 0;
      if (bN <= 1 && aN <= 1) return sp.id;
      return owner ? `${sp.id}@${owner}` : sp.id;
    }
    return owner ? `${owner}.${sp.name}` : sp.name;
  };

  const before = new Map<string, SubprocessDecl>();
  const after = new Map<string, SubprocessDecl>();
  for (const sp of beforeModel.subprocesses) before.set(keyOf(sp), sp);
  for (const sp of afterModel.subprocesses) after.set(keyOf(sp), sp);

  const out = new Map<string, SubprocessDiff>();
  const all = new Set<string>([...before.keys(), ...after.keys()]);
  for (const k of all) {
    const b = before.get(k);
    const a = after.get(k);
    if (b && a) out.set(k, matched(k, b, a, beforeIndex, afterIndex, beforeBodies, afterBodies));
    else if (a) out.set(k, addedSub(k, a, afterIndex, afterBodies));
    else if (b) out.set(k, removedSub(k, b, beforeIndex, beforeBodies));
  }
  return out;
}

function matched(
  key: string,
  b: SubprocessDecl,
  a: SubprocessDecl,
  beforeIndex: Map<string, StableId>,
  afterIndex: Map<string, StableId>,
  beforeBodies: Map<string, Flow>,
  afterBodies: Map<string, Flow>,
): SubprocessDiff {
  const bk = stepKeys(bodyOf(b, beforeBodies), beforeIndex);
  const ak = stepKeys(bodyOf(a, afterBodies), afterIndex);
  const added: StepKey[] = ak.filter((k) => !bk.some((x) => keyEquals(x, k)));
  const removed: StepKey[] = bk.filter((k) => !ak.some((x) => keyEquals(x, k)));
  const reordered =
    added.length === 0 &&
    removed.length === 0 &&
    bk.some((k, i) => !ak[i] || !keyEquals(k, ak[i]));
  const nameChanged = b.name !== a.name;
  const ownerChanged = !pathsEqual(b.ownerPath, a.ownerPath);
  const anyInner = added.length > 0 || removed.length > 0 || reordered;
  let status: Status = "unchanged";
  if (nameChanged || ownerChanged) status = "renamed";
  else if (anyInner) status = "modified";
  return {
    id: key,
    name: a.name,
    status,
    nameChange: nameChanged ? { from: b.name, to: a.name } : undefined,
    ownerPath: a.ownerPath,
    ownerChange: ownerChanged
      ? { from: b.ownerPath ?? [], to: a.ownerPath ?? [] }
      : undefined,
    steps: { added, removed, modified: [], reordered },
    sourceFile: a.source?.file,
  };
}

function addedSub(
  key: string,
  sp: SubprocessDecl,
  index: Map<string, StableId>,
  bodies: Map<string, Flow>,
): SubprocessDiff {
  return {
    id: key,
    name: sp.name,
    status: "added",
    ownerPath: sp.ownerPath,
    steps: {
      added: stepKeys(bodyOf(sp, bodies), index),
      removed: [],
      modified: [],
      reordered: false,
    },
    sourceFile: sp.source?.file,
  };
}

function removedSub(
  key: string,
  sp: SubprocessDecl,
  index: Map<string, StableId>,
  bodies: Map<string, Flow>,
): SubprocessDiff {
  return {
    id: key,
    name: sp.name,
    status: "removed",
    ownerPath: sp.ownerPath,
    steps: {
      added: [],
      removed: stepKeys(bodyOf(sp, bodies), index),
      modified: [],
      reordered: false,
    },
    sourceFile: sp.source?.file,
  };
}
